import React, {useEffect, useState} from "react";
import Row from "../../../Common/Row";
import Skeleton from "@mui/material/Skeleton";
import Space from "../../../Common/Space";
import Column from "../../../Common/Column";
import {Typography, useTheme, Box} from "@mui/material";
import {observer} from "mobx-react-lite";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {categoryStore} from "../../../../Stores/CategoryStore";
import {deepCopy} from "../../../../Constants/Constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {IconFactory} from "../../../Shared/Icons/Factory/IconFactory";

const CategoryFilterSkeletonDekstop = () => {
    // Generate loading rows
    let rows = [];
    const widths = ["95px", "120px", "135px", "95px", "140px", "130px", "85px", "100px", "120px", "80px", "110px", "140px"]
    for (let i = 0; i < widths.length; i++) {
        rows.push(
            <React.Fragment key={"category-filter-web-skeleton-" + i}>
                <Row>
                    <Skeleton
                        variant="rectangular"
                        width="35px"
                        height="35px"
                        sx={{
                            borderRadius: "5px",
                        }}
                    />
                    <Space width="15px"/>
                    <Skeleton
                        variant="text"
                        width={widths[i]}
                    />
                </Row>
                <Space height="15px" />
            </React.Fragment>
        )
    }

    return <Column sx={{width: "fit-content", paddingRight: "30px"}}>
        <Typography variant="h5">Kategorier</Typography>
        <Space height="10px" />
        {rows}
    </Column>
}

// Lower level categories all look the same and are created recursively
const SubcategoryTree = ({subcategories, prefix, level}) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    return <>
        {level == 2 && <Space height="5px"/>}
        {subcategories.map(subcategory => {
            return <React.Fragment key={"search-results-subcategories-" + level + "-" + subcategory.name}>
                <Typography
                    sx={{
                        cursor: "pointer",
                        marginLeft: `calc(${(level-1)*20 + 40}px)`,
                        fontSize: "15px",
                        lineHeight: "17px",
                        color: theme.palette.jagtred.main,
                    }}
                    style={subcategory.isRefined ? {fontWeight: "bold"} : {}}
                    onClick={() => {
                        // Clear all parameters except 'category', 'price', 'radius', and 'latLng'
                        Array.from(searchParams.keys()).forEach(key => {
                            if (key !== 'category' && key !== 'price' && key !== 'radius' && key !== 'latLng') {
                                searchParams.delete(key);
                            }
                        });

                        if (subcategory.isRefined) {
                            searchParams.set("category", prefix);
                        } else {
                            searchParams.set("category", prefix + " > " + subcategory.name);
                        }

                        setSearchParams(searchParams);
                    }}
                >{subcategory.name}</Typography>
                <Space height="10px"/>
                {subcategory.isRefined && subcategory.children.length > 0 && (
                    <SubcategoryTree subcategories={subcategory.children} prefix={prefix + " > " + subcategory.name} level={level + 1}/>
                )}
            </React.Fragment>
        })}
    </>
}

const CategoryTree = ({categories}) => {
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    return categories.map(category => {
        return <React.Fragment key={"search-results-categories-" + category.name}>
            <Row sx={{alignItems: "center", marginTop: "10px"}}>
                <IconFactory icon={category.name} width="30px"/>
                <Space width="10px"/>
                <Typography
                    sx={{cursor: "pointer", fontSize: "18px"}}
                    style={category.isRefined ? {fontWeight: "bold", color: theme.palette.jagtred.main} : {}}
                    onClick={() => {
                        // If we are not on the search page, navigate to it
                        if (!location.pathname.includes("search")) {
                            return navigate("/search?category=" + category.name)
                        }

                        // Clear all parameters except 'category'
                        Array.from(searchParams.keys()).forEach(key => {
                            if (key !== 'category') {
                                searchParams.delete(key);
                            }
                        });

                        if (category.isRefined) {
                            searchParams.delete("category");
                        } else {
                            searchParams.set("category", category.name);
                        }

                        setSearchParams(searchParams);
                    }}
                >{category.name}</Typography>
            </Row>
            <Space height="10px"/>
            {category.isRefined && category.children.length > 0 && (
                <SubcategoryTree
                    subcategories={category.children}
                    prefix={category.name}
                    level={2}
                />
            )}
        </React.Fragment>
    })
}

export const CategoryFilterDesktop = observer(() => {
    const [allCategories, setAllCategories] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const categoryParam = searchParams.get("category");

    const isRefined = allCategories.some(c => c.isRefined);
    const categoriesToShow = isRefined ? allCategories.filter(c => c.isRefined) : allCategories;

    useEffect(() => {
        if (!categoryStore.isInitialized) return;

        const newState = deepCopy(categoryStore.categoryTree);
        const categoryFilter = categoryParam?.split(" > ");

        // Mark refined categories
        let level = newState;
        for (let i = 0; i < categoryFilter?.length; i++) {
            const category = level.find(c => c.name === categoryFilter[i]);
            if (category == null) break;
            category.isRefined = true;
            level = category.children;
        }

        setAllCategories(newState);
    }, [categoryStore.isInitialized, categoryParam]);

    if (!categoryStore.isInitialized)
        return <CategoryFilterSkeletonDekstop/>;

    return <Box sx={{minWidth: "200px", maxWidth: "200px", paddingRight: "30px"}}>
        <Typography variant="h5" sx={{fontWeight: "bold", paddingBottom: "10px"}}>Kategorier</Typography>
        {isRefined && (
            <ArrowBackIcon
                sx={{cursor: "pointer"}}
                onClick={() => {
                    // Remove all search parameters
                    setSearchParams(new URLSearchParams());
                }}
            />
        )}
        <CategoryTree categories={categoriesToShow}/>
    </Box>
});